import type { Permissions as BasePermissions } from '@vlabs/api/artemy/model';

import type { ExtractPermissions } from './typeUtils';

export type PointPermissions = Omit<BasePermissions, 'pages'> & { pages: PointPermissionPage[] };
export type EditPermissionsFormProps = PointPermissions;

type FieldsWithPermissions = Omit<EditPermissionsFormProps, 'visibility' | 'pages'>;
export type FieldsWithPermissionsKeys = keyof Omit<EditPermissionsFormProps, 'visibility' | 'pages'>;

export type NonNullableFieldsWithPermissions = Required<FieldsWithPermissions>;
export type FilterPropsByPermission<Permission> = {
  [K in keyof NonNullableFieldsWithPermissions as NonNullableFieldsWithPermissions[K] extends
  ExtractPermissions<NonNullableFieldsWithPermissions[K], Permission>
    ? K
    : never
  ]: NonNullableFieldsWithPermissions[K];
};

const PointPermissionFieldsMap = {
  users: true,
  roles: true,
  departments: true,
  permission_tags: true,
  // face: true,
  card: true,
  card_document: true,
  stream: true,
  group: true,
  handler: true,
  event_fs: true,
  alarm: true,
  list: true,
  task: true,
  notification: true,
  audit_log: true,
  audit_config: true,
  session: true,
  card_custom_field: true,
  event_send_rule: true,
} satisfies Record<FieldsWithPermissionsKeys, true>;
export const PointPermissionFields = Object.keys(PointPermissionFieldsMap) as FieldsWithPermissionsKeys[];
export type PointPermissionFieldKeys = typeof PointPermissionFields[number];

const PointReadablePermissionFieldsMap = {
  users: true,
  roles: true,
  departments: true,
  permission_tags: true,
  // face: true,
  card: true,
  card_document: true,
  stream: true,
  group: true,
  handler: true,
  event_fs: true,
  alarm: true,
  list: true,
  task: true,
  notification: true,
  audit_log: true,
  audit_config: true,
  session: true,
  card_custom_field: true,
  event_send_rule: true,
} satisfies Record<keyof FilterPropsByPermission<'read'>, true>;
export const PointReadablePermissionFields = Object.keys(PointReadablePermissionFieldsMap) as (keyof FilterPropsByPermission<'read'>)[];
export type PointReadablePermissionFieldKeys = typeof PointReadablePermissionFields[number];

const PointCreatablePermissionFieldsMap = {
  users: true,
  roles: true,
  departments: true,
  permission_tags: true,
  // face: true,
  card: true,
  card_document: true,
  stream: true,
  group: true,
  handler: true,
  event_fs: true,
  // alarm: true,
  list: true,
  task: true,
  notification: true,
  // audit_log: true,
  audit_config: true,
  // session: true,
  card_custom_field: true,
  event_send_rule: true,
} satisfies Record<keyof FilterPropsByPermission<'create'>, true>;
export const PointCreatablePermissionFields = Object.keys(PointCreatablePermissionFieldsMap) as (keyof FilterPropsByPermission<'create'>)[];
export type PointCreatablePermissionFieldKeys = typeof PointCreatablePermissionFields[number];

const PointUpdateablePermissionFieldsMap = {
  users: true,
  roles: true,
  departments: true,
  // permission_tags: true,
  // face: true,
  card: true,
  card_document: true,
  stream: true,
  group: true,
  handler: true,
  // event_fs: true,
  alarm: true,
  list: true,
  // task: true,
  notification: true,
  // audit_log: true,
  audit_config: true,
  session: true,
  card_custom_field: true,
  event_send_rule: true,
} satisfies Record<keyof FilterPropsByPermission<'update'>, true>;
export const PointUpdateablePermissionFields = Object.keys(PointUpdateablePermissionFieldsMap) as (keyof FilterPropsByPermission<'update'>)[];
export type PointUpdateablePermissionFieldKeys = typeof PointUpdateablePermissionFields[number];

const PointDeletablePermissionFieldsMap = {
  // users: true,
  roles: true,
  departments: true,
  permission_tags: true,
  // face: true,
  card: true,
  card_document: true,
  stream: true,
  group: true,
  handler: true,
  event_fs: true,
  // alarm: true,
  list: true,
  task: true,
  notification: true,
  audit_log: true,
  audit_config: true,
  session: true,
  card_custom_field: true,
  event_send_rule: true,
} satisfies Record<keyof FilterPropsByPermission<'delete'>, true>;
export const PointDeletablePermissionFields = Object.keys(PointDeletablePermissionFieldsMap) as (keyof FilterPropsByPermission<'delete'>)[];
export type PointDeletablePermissionFieldKeys = typeof PointDeletablePermissionFields[number];

export const PointPermissionPages = {
  operator_panel: 'operator_panel',
  events_fs_archive: 'events_fs_archive',
  events_va_archive: 'events_va_archive',
  events_cars_archive: 'events_cars_archive',
  event_fs_details: 'event_fs_details',
  event_va_details: 'event_va_details',
  event_cars_details: 'event_cars_details',
  videowall: 'videowall',
  search: 'search',
  statistics_fs: 'statistics_fs',
  cards: 'cards',
  cards_details: 'cards_details',
  lists: 'lists',
  streams_fs: 'streams_fs',
  streams_va: 'streams_va',
  handlers: 'handlers',
  handler_details: 'handler_details',
  tasks: 'tasks',
  monitoring: 'monitoring',
  licenses: 'licenses',
  notifications: 'notifications',
  departments: 'departments',
  users: 'users',
  roles: 'roles',
  permission_tags: 'permission_tags',
} as const;
export type PointPermissionPage = typeof PointPermissionPages[keyof typeof PointPermissionPages];

// страницы, которые не входят в первый этап POINT 3
export const PointHiddenPages = [
  'event_fs_details',
  'event_va_details',
  'event_cars_details',
  'events_va_archive',
  'events_cars_archive',
  'streams_fs',
  'handler_details',
  'tasks',
  'notifications',
] as const satisfies readonly PointPermissionPage[];
export const PointVisiblePages = Object.values(PointPermissionPages).filter(
  (page): page is PointPermissionPage => !(PointHiddenPages as readonly PointPermissionPage[]).includes(page),
);

// объекты системы, права которых скрыты на первом этапе POINT 3
export const PointHiddenPermissionFields = [
  'card_document',
  'alarm',
  'task',
  'notification',
  'audit_log',
  'audit_config',
  'session',
  'card_custom_field',
  'event_send_rule',
] as const satisfies readonly FieldsWithPermissionsKeys[];

const isVisibleField = (field: FieldsWithPermissionsKeys): boolean => (
  !(PointHiddenPermissionFields as readonly FieldsWithPermissionsKeys[]).includes(field)
);

export const PointVisiblePermissionFields = PointPermissionFields.filter(isVisibleField);
export const PointVisibleReadablePermissionFields = PointReadablePermissionFields.filter(isVisibleField);
export const PointVisibleCreatablePermissionFields = PointCreatablePermissionFields.filter(isVisibleField);
export const PointVisibleUpdateablePermissionFields = PointUpdateablePermissionFields.filter(isVisibleField);
export const PointVisibleDeletablePermissionFields = PointDeletablePermissionFields.filter(isVisibleField);
